/**
 * RaceLeaders — tabbed leaderboard panel in the sidebar.
 *
 * v1 renders a calm empty state ("Live splits appear when the race starts").
 * Live data is a follow-up via the Sportraxs skill — see PRIMITIVES_CATALOG.md
 * GpsLeaderPin entry for the integration shape.
 *
 * Config supports a static `entries` array so the Director can manually pin
 * a leaderboard in the layout (useful for past-event recaps or schedules).
 */

import { useState } from 'react'
import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import { useEvent, useStandings } from '../../destination'

interface LeaderEntry {
  pos: number
  name: string
  bib?: string
  gap?: string
  time: string
}

interface LeaderTab {
  key: string
  label: string
  entries: LeaderEntry[]
}

interface RaceLeadersConfig {
  title?: string
  label?: string
  sourceKey?: string
  limit?: number
  tabs?: LeaderTab[]
  /** Empty-state copy shown when no tabs / no entries. */
  emptyText?: string
}

function posClass(pos: number) {
  if (pos === 1) return 'lv-leader-pos lv-leader-pos-gold'
  if (pos === 2) return 'lv-leader-pos lv-leader-pos-silver'
  if (pos === 3) return 'lv-leader-pos lv-leader-pos-bronze'
  return 'lv-leader-pos'
}

export function ResultsView({ config }: PrimitiveProps) {
  const event = useEvent()
  const cfg = config as RaceLeadersConfig
  const standings = useStandings({
    sourceKey: cfg.sourceKey,
    limit: cfg.limit ?? 5,
  })
  const providerEntries: LeaderEntry[] = standings.data.map((standing, index) => ({
    pos: standing.rank ?? index + 1,
    name: standing.displayName,
    bib: standing.bib ?? undefined,
    gap: standing.gap ?? undefined,
    time: standing.elapsedTime ?? '—',
  }))
  const tabs = cfg.tabs ?? (
    providerEntries.length > 0
      ? [{key: 'live', label: 'Overall', entries: providerEntries}]
      : []
  )
  const title = cfg.label ?? cfg.title ?? 'Race leaders'
  const [activeKey, setActiveKey] = useState(tabs[0]?.key ?? '')

  const activeTab = tabs.find((t) => t.key === activeKey) ?? tabs[0]

  if (!activeTab || activeTab.entries.length === 0) {
    const emptyText =
      cfg.emptyText ??
      (event.startsAt
        ? `Live splits appear here once the race starts on ${new Date(event.startsAt).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}.`
        : 'Live splits appear here once the race starts.')
    return (
      <div className="lv-leaders">
        <div className="lv-leaders-head">
          <span className="lv-leaders-title">{title}</span>
        </div>
        <div className="lv-leaders-empty">{emptyText}</div>
      </div>
    )
  }

  return (
    <div className="lv-leaders">
      <div className="lv-leaders-head">
        <span className="lv-leaders-title">{title}</span>
        {tabs.length > 1 && (
          // Pressed toggle buttons in a named group: they swap the list below rather than own tab panels.
          <div className="lv-leaders-tabs" role="group" aria-label={title}>
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-pressed={t.key === activeKey}
                onClick={() => setActiveKey(t.key)}
                className={`lv-leaders-tab${t.key === activeKey ? ' lv-leaders-tab-active' : ''}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {activeTab.entries.map((entry) => (
        <div key={`${entry.pos}-${entry.name}`} className="lv-leader">
          <span className={posClass(entry.pos)}>{entry.pos}</span>
          <span className="lv-leader-name">
            {entry.name}
            {entry.bib && <span className="lv-leader-bib">#{entry.bib}</span>}
          </span>
          <span className="lv-leader-gap">{entry.gap ?? '—'}</span>
          <span className="lv-leader-time">{entry.time}</span>
        </div>
      ))}
    </div>
  )
}

registerPrimitive('ResultsView', ResultsView)
registerPrimitive('RaceLeaders', ResultsView)
